/**
 * Доля хвоста публичного рендерера: вид `downsell_popup` (предложение со
 * скидкой).
 *
 * WEB-2920, заход 3: перенесено из `PublicBlockRendererExtended.tsx` слово в
 * слово вместе с попапом (`DownsellPopup`) и карточкой предложения
 * (`DownsellCard`, дверь оплаты: `usePaywallCheckout` + `useCheckoutUnavailable`
 * — сторож WEB-2009 видит её здесь обходом всего дерева). Метка
 * `case 'downsell_popup'` остаётся в фасаде; ветка вида — функция отрисовки
 * `renderDownsellPopup(ctx)`, цены Stripe и цвет текста приходят
 * объектом-контекстом (`ExtendedRenderContext`). Эту долю импортирует только
 * фасад: она едет в ленивом куске `extended`, а не в первом экране
 * (самопроверка обхода).
 */
import React, { useState } from 'react';
import { createPortal } from 'react-dom';
// WEB-1043: один источник переноса оформления на порталы для ОБОИХ
// потребителей — попапа даунселла (WEB-712) и cookie-баннера.
import { usePortalSurfaceVars } from '../usePortalSurfaceVars';
import type { VariableContext } from '@/player/variable-context';
import { RichTextContent } from '../RichTextContent';
import { asRichField, hasRichText, type RichField } from '@/types/rich-text';
import {
  blockTextRoles,
  type DownsellPopupTextStyles,
  textRoleStyle,
} from '../semanticTextRoles';
import {
  type PublicStripePrice,
  formatStripePrice,
  findStripePrice,
} from '@/utils/formatStripePrice';
import {
  useDownsellTrigger,
  type DownsellTriggerName,
} from '../downsellTriggerContext';
import { usePaywallCheckout } from '../paywallCheckoutContext';
import { useCheckoutUnavailable } from '@/components/paywall-runtime/checkoutAvailability';
import { checkoutErrorText } from '@/components/paywall-runtime/checkoutErrorText';
import { useEditorPreview } from '../EditorPreviewContext';
import { getPublicQuizStrings } from '@/utils/publicQuizStrings';
import { resolvePaywallText } from '@/utils/portalLegacyDefaults';
import { pickStrings } from '@/i18n';
// WEB-2057: подсказки АВТОРУ — из своего маленького словаря. Полный словарь
// конструктора этому файлу нельзя: его рисует ПУБЛИЧНАЯ страница покупателя.
import { authorHintsStrings } from '@/i18n/dictionaries/authorHints';
import { aiFields } from '../aiShapedFields';
import type { ExtendedRenderContext } from '../PublicBlockRendererExtended';

interface DownsellPopupProps {
  /** Empty/undefined = legacy behavior: always render inline, no gating. */
  triggers: DownsellTriggerName[] | undefined;
  title?: RichField;
  text?: RichField;
  ctaLabel: RichField;
  price?: PublicStripePrice;
  /** WEB-703 п.1: raw priceId the CTA charges (downsellPriceId) — for checkout. */
  checkoutPriceId?: string;
  primaryColor: string;
  /** WEB-672 2a: interpolation ctx (title, text и — с WEB-2923 — ctaLabel). */
  varCtx?: VariableContext;
  /**
   * WEB-1692 юнит 5 — накладки четырёх текстов карточки предложения
   * (заголовок, текст, цена, надпись кнопки). Пустые объекты, пока автор
   * ничего не задал: вид старой воронки не меняется ни на байт.
   */
  textStyles: DownsellPopupTextStyles;
}

/**
 * WEB-447 §4.3(b) — the downsell offer card, with trigger-gated visibility.
 * Extracted from the `downsell_popup` switch case because it needs
 * useDownsellTrigger() (a hook), which cannot be called conditionally inside
 * a switch statement in the parent render function.
 */
function DownsellPopup({
  triggers,
  title,
  text,
  ctaLabel,
  price,
  checkoutPriceId,
  primaryColor,
  varCtx,
  textStyles,
}: DownsellPopupProps): React.ReactElement | null {
  const { hasFired, dismissed, dismiss, suppressed } = useDownsellTrigger();
  const { isEditorPreview, downsellPreviewVisible } = useEditorPreview();
  // WEB-712: якорь остаётся на месте блока в потоке — с него снимаются
  // переменные оформления для узла портала (см. usePortalSurfaceVars).
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const portalVars = usePortalSurfaceVars(anchor);

  const hasTriggers = !!triggers && triggers.length > 0;
  const triggerFired = triggers?.some((name) => hasFired(name)) ?? false;

  // QA WEB-673: с триггерами попап в превью не показывался вовсе («не видно
  // ничего») — в editor-превью показываем карточку inline по кнопке
  // «Показать» из панели, иначе компактный плейсхолдер.
  if (isEditorPreview && hasTriggers) {
    if (!downsellPreviewVisible) {
      return (
        <div
          data-testid="downsell-editor-placeholder"
          style={{
            marginBottom: 16,
            padding: '10px 12px',
            borderRadius: 'var(--border-radius-md, 8px)',
            border: '1px dashed var(--color-border-primary, #d9d9d9)',
            background: 'var(--color-bg-surface, #fafafa)',
            color: 'var(--color-text-secondary, #8c8c8c)',
            fontSize: 12,
            textAlign: 'center',
          }}
        >
          {pickStrings(authorHintsStrings).downsellPopupPreviewHint}
        </div>
      );
    }
    // WEB-1709 (ревизия пачки 1): карточка отдаётся БЕЗ лишней обёртки с
    // собственным `marginBottom: 16`. Прежде обёртка дублировала число: у
    // карточки внутри своё такое же поле, и правило гашения
    // `[data-block-spacing] > *` доставало только до обёртки — заданное
    // автором расстояние (например 0) всё равно упиралось в 16px от внука.
    // Вид без заданного расстояния не меняется: поля схлопывались в те же 16.
    return (
      <DownsellCard
        title={title}
        text={text}
        ctaLabel={ctaLabel}
        price={price}
        checkoutPriceId={checkoutPriceId}
        primaryColor={primaryColor}
        varCtx={varCtx}
        textStyles={textStyles}
      />
    );
  }

  // Backward-compat (no triggers configured): render inline, always visible,
  // same as before this atom.
  if (!hasTriggers) {
    return (
      <DownsellCard
        title={title}
        text={text}
        ctaLabel={ctaLabel}
        price={price}
        checkoutPriceId={checkoutPriceId}
        primaryColor={primaryColor}
        varCtx={varCtx}
        textStyles={textStyles}
      />
    );
  }

  // WEB-2030: гашение сильнее взведённого повода. Отказ кассы, который дешёвым
  // вариантом не лечится (чужая метка покупателя, нет платёжки), гасит
  // предложение целиком — иначе человек, уже упёршийся в отказ, получает
  // вторым экраном предложение, которое заведомо не сработает: второй тупик
  // подряд (WEB-933). Стоит НИЖЕ ветки `!hasTriggers`: карточка без поводов —
  // обычный блок страницы, и прятать её посреди сеанса нечего.
  if (suppressed || dismissed || !triggerFired) return null;

  const overlay = (
    <div
      data-testid="downsell-popup-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(0, 0, 0, 0.45)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        padding: 16,
        // WEB-712: портал обрывает наследование от корня поверхности воронки
        // (переменные там инлайновые) — возвращаем их явно.
        ...portalVars,
        fontFamily: 'var(--font-family-primary, Inter, sans-serif)',
      }}
    >
      <div style={{ position: 'relative', maxWidth: 420, width: '100%' }}>
        <button
          data-testid="downsell-close"
          onClick={dismiss}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: -12,
            right: -12,
            width: 32,
            height: 32,
            borderRadius: '50%',
            border: 'none',
            background: 'var(--color-bg-surface, #fff)',
            // WEB-966: подложка светлая — текст со страницы (фото-палитра) невидим.
            color:
              'var(--color-text-on-surface, var(--color-text-primary, inherit))',
            boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
            fontSize: 16,
            fontWeight: 700,
            cursor: 'pointer',
            lineHeight: '32px',
          }}
        >
          &times;
        </button>
        <DownsellCard
          title={title}
          text={text}
          ctaLabel={ctaLabel}
          price={price}
          checkoutPriceId={checkoutPriceId}
          primaryColor={primaryColor}
          varCtx={varCtx}
          textStyles={textStyles}
        />
      </div>
    </div>
  );

  return (
    <>
      {/* WEB-712: место блока в потоке. Ничего не рисует и не занимает
          места — служит точкой, с которой снимается каскад оформления. */}
      <span
        ref={setAnchor}
        data-testid="downsell-popup-anchor"
        aria-hidden="true"
        style={{ display: 'none' }}
      />
      {/* WEB-712: корень экрана несёт заливку анимации появления
          (.funnel-screen-in, animation-fill-mode: both), а анимированный
          transform вычисляется в матрицу — то есть НЕ в `none`. Такой предок
          становится содержащим блоком для position:fixed, и подложка попапа
          вместо вьюпорта получала размер экрана: покупатель видел затемнение
          без карточки. Портал в <body> выносит попап из-под любого такого
          предка. С 21.10.2026 заливка — backwards, матрица после конца
          анимации не остаётся, но во время анимации появления transform на
          корне действует — портал по-прежнему нужен. */}
      {typeof document !== 'undefined'
        ? createPortal(overlay, document.body)
        : overlay}
    </>
  );
}

interface DownsellCardProps {
  title?: RichField;
  text?: RichField;
  ctaLabel: RichField;
  price?: PublicStripePrice;
  /** WEB-703 п.1: priceId the CTA charges (downsellPriceId). */
  checkoutPriceId?: string;
  primaryColor: string;
  varCtx?: VariableContext;
  /** WEB-1692 юнит 5: накладки четырёх текстов карточки. */
  textStyles: DownsellPopupTextStyles;
}

function DownsellCard({
  title,
  text,
  ctaLabel,
  price,
  checkoutPriceId,
  primaryColor,
  varCtx,
  textStyles,
}: DownsellCardProps): React.ReactElement {
  // WEB-703 п.1: the CTA charges via the paywall's single money-path. `null`
  // outside a live paywall (quiz/editor-preview) → the button is inert (design
  // is being previewed, not sold).
  const startCheckout = usePaywallCheckout();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // WEB-2009 (хвост WEB-1989 шаг 2): третья дверь оплаты после BuyNowButton
  // и экспресс-кошельков — CTA даунселла тоже умеет списать деньги
  // (startCheckout) и обязана слушать тот же признак: показ пришёл из копии
  // на краю, живого API нет, чекаут не пройдёт.
  const checkoutUnavailable = useCheckoutUnavailable();

  const handleClick = async () => {
    if (!startCheckout || isLoading || checkoutUnavailable) return;
    setIsLoading(true);
    setError(null);
    // fireDownsellOnFailure:false — never re-trigger the downsell from inside it.
    const result = await startCheckout(checkoutPriceId, {
      fireDownsellOnFailure: false,
    });
    // WEB-933 п.2: единый текст покупателю (checkoutErrorText).
    setError(checkoutErrorText(result, varCtx?.system?.locale));
    setIsLoading(false);
  };

  return (
    <div
      data-testid="downsell-popup"
      style={{
        border: '2px solid var(--color-error, #ff4d4f)',
        borderRadius: 'var(--border-radius-lg, 12px)',
        padding: '16px 20px',
        marginBottom: 16,
        textAlign: 'center',
        background: 'var(--color-bg-surface, #fff)',
        // WEB-966: подложка светлая — текст со страницы (фото-палитра) невидим.
        color:
          'var(--color-text-on-surface, var(--color-text-primary, inherit))',
      }}
    >
      {hasRichText(title) && (
        <h3
          style={{
            fontSize: 18,
            fontWeight: 700,
            margin: '0 0 8px',
            color: primaryColor,
            // WEB-1692 юнит 5: накладка автора ПОСЛЕ прежних строк — пустая
            // при незаданных полях, и вид старой воронки не меняется.
            ...textStyles.title,
          }}
        >
          <RichTextContent value={title} varCtx={varCtx} />
        </h3>
      )}
      {hasRichText(text) && (
        <p
          style={{
            fontSize: 14,
            color: 'var(--color-text-secondary, #8c8c8c)',
            margin: '0 0 12px',
            ...textStyles.secondary,
          }}
        >
          <RichTextContent value={text} varCtx={varCtx} />
        </p>
      )}
      {price && (
        <div
          style={{
            // 22px крупнее заголовка (18) НАМЕРЕННО: ради скидочной цены попап
            // и показывается. Своя пара полей у неё именно поэтому — общая
            // настройка схлопнула бы иерархию выгоды.
            fontSize: 22,
            fontWeight: 700,
            marginBottom: 12,
            color: primaryColor,
            ...textStyles.price,
          }}
        >
          {formatStripePrice(price)}
        </div>
      )}
      {error && (
        <div
          data-testid="downsell-cta-error"
          style={{
            color: 'var(--color-error, #ff4d4f)',
            fontSize: 13,
            marginBottom: 10,
          }}
        >
          {error}
        </div>
      )}
      <button
        data-testid="downsell-cta"
        onClick={handleClick}
        disabled={isLoading || checkoutUnavailable}
        style={{
          padding: '10px 28px',
          background: 'var(--color-error, #ff4d4f)',
          color: 'var(--color-on-primary, #fff)',
          border: 'none',
          borderRadius: 'var(--border-radius-md, 8px)',
          fontSize: 15,
          fontWeight: 600,
          cursor: isLoading || checkoutUnavailable ? 'not-allowed' : 'pointer',
          opacity: isLoading || checkoutUnavailable ? 0.7 : 1,
          ...textStyles.cta,
        }}
      >
        {/* WEB-2923: подпись кнопки — текст автора, через подстановку. */}
        <RichTextContent value={ctaLabel} varCtx={varCtx} />
      </button>
    </div>
  );
}

/** Ветка `downsell_popup` фасада — тело `case` слово в слово. */
export function renderDownsellPopup(
  ctx: ExtendedRenderContext,
): React.ReactElement {
  const { block, varCtx, primaryColor, stripePrices } = ctx;
  const ai = aiFields(block);
  // WEB-1692 юнит 5. Роли берёт общий механизм, свои пары (цена и
  // надпись кнопки) читаются ЗДЕСЬ под собственными именами — этого
  // требует сторож контракта имён WEB-1500 у блоков с реестром пар.
  const popupRoles = blockTextRoles(block, 'downsell_popup');
  // WEB-1905 (ревизия пачки 29): старые английские умолчания
  // конструктора до 05.09 в содержимом = незаданные → словарь по языку.
  const legacyDefaults = getPublicQuizStrings(
    varCtx?.system?.locale,
  ).blockDefaults;
  const title = asRichField(
    resolvePaywallText(
      block.downsellTitle ?? ai.title,
      legacyDefaults.downsellTitle,
    ),
  );
  const text = asRichField(block.downsellText ?? ai.text);
  const priceId = block.downsellPriceId ?? ai.priceId;
  const ctaLabel =
    asRichField(
      resolvePaywallText(
        block.downsellCtaLabel ?? ai.ctaLabel,
        legacyDefaults.downsellCta,
      ),
    ) ??
    // WEB-1907 (добор): последний английский фолбэк рендерера. Кнопка
    // без подписи бывает у сохранённых воронок и у генерации без этого
    // поля — покупатель испанской воронки читал «Get discount». Ключ
    // тот же, что у умолчания нового блока (blockDefaults.downsellCta).
    getPublicQuizStrings(varCtx?.system?.locale).blockDefaults.downsellCta;
  const price = findStripePrice(stripePrices, priceId);
  // WEB-447 §4.3(b): trigger-based visibility. Empty/undefined
  // downsellTriggers = legacy behavior (always inline, no gating).
  // WEB-1500: та же подстраховка «имя редактора, а если пусто — имя
  // генерации», что у соседних полей выше. Без неё условие показа из
  // ИИ-воронки не доезжало, пустое значение читалось как «показывать
  // всегда» — и скидка была видна каждому посетителю ещё до попытки
  // купить по полной цене. Новые генерации канонизируются на приёме
  // (пара triggers → downsellTriggers в реестре имён), фолбэк держит
  // уже сохранённые воронки.
  return (
    <DownsellPopup
      triggers={block.downsellTriggers ?? ai.triggers}
      title={title}
      text={text}
      ctaLabel={ctaLabel}
      price={price}
      checkoutPriceId={priceId}
      primaryColor={primaryColor}
      varCtx={varCtx}
      // WEB-1692 юнит 5 (репорт владельца 24.08 «не хватает настройки
      // выбора размера текста»): у каждого из четырёх текстов карточки
      // стоял свой явный кегль, поэтому общая настройка не действовала
      // никогда. Заголовок и текст — роли, цена и кнопка — свои пары.
      textStyles={{
        title: popupRoles.title,
        secondary: popupRoles.secondary,
        price: textRoleStyle(
          block.downsellPriceColor,
          block.downsellPriceFontSize,
        ),
        cta: textRoleStyle(block.downsellCtaColor, block.downsellCtaFontSize),
      }}
    />
  );
}
